import {
  AllocationValueTypeEnum,
  AllocationsSelector,
  AssetTransactionTypeEnum,
  Box,
  DEFAULT_SUBACCOUNT_ALLOCATION,
  DateTimePicker,
  Dialog,
  DialogContent,
  DialogFooter,
  FormControlSizes,
  FormGroup,
  HStack,
  NOW,
  NotificationVariants,
  NumberInput,
  ProductTypeEnum,
  SearchSelect,
  Stack,
  Text,
  TextDividerSimple,
  ToggleHorizontal,
  frontendAllocationsToBackendAllocations,
  getErrorMessage,
  productTypeLabel,
  useAssetsContext,
  useGlobalToasts,
  useMarketAccountsContext,
  type Allocation,
  type AllocationChange,
  type AssetTransactionTypeEnum as AssetTransactionTypeEnumType,
  type ErrorResponse,
  type ToggleHorizontalOption,
  type useDisclosureWithContext,
} from '@talos/kyoko';
import { isEmpty } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { useSymbolSelectorWidth } from '../../../components/SymbolSelector/useSymbolSelectorWidth';
import { useSubAccounts } from '../../../providers';
import type { UpdatePositionPrimer } from '../PositionsV3/UpdatePositionsDialog';
import type { CreateAssetTransactionItem } from './assetTransactionsApi';
import { useCreateAssetTransactionMutation } from './assetTransactionsApi';
import { EXCLUDED_ADJUST_POSITION_TYPES, prettifyAssetTransactionTypeEnum } from './assetTransactionTypeOptions';
import { useAdjustMarketAccountPositionsValidation } from './useAdjustMarketAccountPositionsValidation';
import { useUpdateBalancesAccounts } from './useUpdateBalancesAccounts';

export type AllocationMode = 'infer' | 'manual' | 'doNotAllocate';

export interface AdjustMarketAccountPositionsForm {
  type?: AssetTransactionTypeEnumType;
  marketAccount?: string;
  asset?: string;
  amount?: string;
  timestamp?: Date;
  allocationMode: AllocationMode;
  subAccountAllocations: Allocation[];
  allocationValueType: AllocationValueTypeEnum;
}

export type AdjustMarketAccountPositionsDisclosure = ReturnType<typeof useDisclosureWithContext<UpdatePositionPrimer>>;

interface AdjustMarketAccountPositionsDialogProps {
  disclosure: AdjustMarketAccountPositionsDisclosure;
  customMarketAccountOptions?: string[];
}

const ALLOCATION_MODE_OPTIONS: ToggleHorizontalOption<AllocationMode>[] = [
  { value: 'infer', displayName: 'Infer using Allocation Controls' },
  { value: 'manual', displayName: 'Allocate Manually' },
  { value: 'doNotAllocate', displayName: 'Do Not Allocate' },
];

const EXCLUDED_ADJUST_POSITION_TYPES_SET = new Set<AssetTransactionTypeEnumType>(EXCLUDED_ADJUST_POSITION_TYPES);

const TYPE_OPTIONS = (Object.values(AssetTransactionTypeEnum) as AssetTransactionTypeEnumType[]).filter(
  t => !EXCLUDED_ADJUST_POSITION_TYPES_SET.has(t)
);

export const AdjustMarketAccountPositionsDialog = ({
  disclosure,
  customMarketAccountOptions,
}: AdjustMarketAccountPositionsDialogProps) => (
  <Dialog
    {...disclosure}
    title={disclosure.context?.Asset ? 'Adjust Market Account Position' : 'Adjust Market Account Positions'}
    width={420}
    showConfirm={false}
    showCancel={false}
    controlledContent
    showClose
    suppressAutoEnterForm
  >
    {disclosure.context && (
      <AdjustMarketAccountPositionsDialogContent
        disclosure={disclosure}
        context={disclosure.context}
        customMarketAccountOptions={customMarketAccountOptions}
      />
    )}
  </Dialog>
);

const getInitialForm = (primer: UpdatePositionPrimer): AdjustMarketAccountPositionsForm => ({
  type: undefined,
  marketAccount: primer.MarketAccount,
  asset: primer.Asset,
  amount: undefined,
  timestamp: undefined,
  allocationMode: 'infer',
  subAccountAllocations: [DEFAULT_SUBACCOUNT_ALLOCATION],
  allocationValueType: AllocationValueTypeEnum.Percentage,
});

interface AdjustMarketAccountPositionsDialogContentProps {
  disclosure: AdjustMarketAccountPositionsDisclosure;
  context: UpdatePositionPrimer;
  customMarketAccountOptions?: string[];
}

const AdjustMarketAccountPositionsDialogContent = ({
  disclosure,
  context,
  customMarketAccountOptions,
}: AdjustMarketAccountPositionsDialogContentProps) => {
  const { add: addToast } = useGlobalToasts();
  const [createAssetTransaction, { isLoading }] = useCreateAssetTransactionMutation();

  const { assetsList, getAssetDisplaySymbol, resolveSecurityOrCurrency } = useAssetsContext();
  const symbolSelectorDropdownWidth = useSymbolSelectorWidth();

  const getAssetGroup = useCallback(
    (asset: string) => {
      const { currency, security } = resolveSecurityOrCurrency(asset);
      if (currency) {
        return productTypeLabel(ProductTypeEnum.Spot);
      }
      if (security) {
        return productTypeLabel(security.ProductType);
      }
      return 'Unknown';
    },
    [resolveSecurityOrCurrency]
  );

  const getAssetDescription = useCallback(
    (asset: string) => {
      const { currency, security } = resolveSecurityOrCurrency(asset);
      return (currency ?? security)?.Description ?? '';
    },
    [resolveSecurityOrCurrency]
  );
  const { marketAccountsByMarket, marketAccountsByName } = useMarketAccountsContext();
  const { accounts: internalMarketAccountNames } = useUpdateBalancesAccounts();
  const { allSubAccountBooks, subAccountsByName } = useSubAccounts();

  const [form, setForm] = useState<AdjustMarketAccountPositionsForm>(() => getInitialForm(context));

  // Reset form on each open (openId is a new UUID every time open() is called)
  useEffect(() => {
    setForm(getInitialForm(context));
  }, [disclosure.openId, context]);

  const { errors, touched, setTouched, setAllTouched } = useAdjustMarketAccountPositionsValidation(form);

  const updateForm = useCallback((update: Partial<AdjustMarketAccountPositionsForm>) => {
    setForm(prev => ({ ...prev, ...update }));
  }, []);

  const marketAccountOptions = useMemo(() => {
    if (customMarketAccountOptions) {
      return customMarketAccountOptions;
    }
    if (internalMarketAccountNames?.length) {
      return internalMarketAccountNames;
    }
    return marketAccountsByMarket.get('internal')?.map(ma => ma.Name) ?? [];
  }, [customMarketAccountOptions, internalMarketAccountNames, marketAccountsByMarket]);

  const assetOptions = useMemo(() => assetsList.map(a => a.Symbol), [assetsList]);

  const subAccountOptions = useMemo(() => allSubAccountBooks?.map(s => s.Name) ?? [], [allSubAccountBooks]);

  const getMarketAccountLabel = useCallback(
    (name: string | undefined) => (name ? (marketAccountsByName.get(name)?.DisplayName ?? name) : (name ?? '')),
    [marketAccountsByName]
  );

  const handleConfirm = useCallback(async () => {
    if (!isEmpty(errors) || !form.marketAccount || !form.asset || !form.amount || !form.timestamp || !form.type) {
      return;
    }

    let subAccount: string | undefined;
    let allocation = undefined;

    if (form.allocationMode === 'manual') {
      const { subAccountAllocations, allocationValueType, amount } = form;
      if (subAccountAllocations.length > 1) {
        allocation = frontendAllocationsToBackendAllocations({
          subAccountAllocations,
          subAccounts: allSubAccountBooks,
          allocationValueType,
          quantity: amount,
        });
      } else {
        subAccount = subAccountAllocations[0]?.subAccount || undefined;
      }
    }

    const payload: CreateAssetTransactionItem = {
      TransactionID: uuid(),
      TransactTime: form.timestamp.toISOString(),
      Type: form.type,
      MarketAccount: form.marketAccount,
      Asset: form.asset,
      Amount: form.amount,
      AllocationStatus: form.allocationMode === 'infer' ? 'Unallocated' : 'Manual',
    };
    if (subAccount != null) {
      payload.SubAccount = subAccount;
    }
    if (allocation != null) {
      payload.Allocation = allocation;
    }

    try {
      const created = await createAssetTransaction([payload]).unwrap();

      addToast({
        text:
          created.length === 1
            ? 'Successfully applied 1 transaction.'
            : `Successfully applied ${created.length} transactions.`,
        variant: NotificationVariants.Positive,
      });
      disclosure.close();
    } catch (e: unknown) {
      const detail = getErrorMessage(e as ErrorResponse) ?? (e instanceof Error ? e.message : undefined) ?? String(e);
      addToast({
        text: `Failed to post balance adjustment: ${detail}`,
        variant: NotificationVariants.Negative,
      });
    }
  }, [errors, form, allSubAccountBooks, createAssetTransaction, addToast, disclosure]);

  const handleAllocationsChange = useCallback(
    (newAllocations: Allocation[], _change: AllocationChange) => {
      updateForm({ subAccountAllocations: newAllocations });
      setTouched(prev => ({ ...prev, subAccountAllocations: true }));
    },
    [updateForm, setTouched]
  );

  const handleAllocationsValueTypeChange = useCallback(
    (newType: AllocationValueTypeEnum) => updateForm({ allocationValueType: newType }),
    [updateForm]
  );

  return (
    <>
      <DialogContent overflow="hidden" style={{ order: 2, height: '100%', overflow: 'auto' }}>
        <Stack gap="spacing0" w="100%">
          <Text textAlign="left" size="fontSizeSm" mb="spacing16">
            Create a new Asset Transaction for a Market Account.
          </Text>

          <Box my="spacing4">
            <TextDividerSimple text="Details" />
          </Box>

          <FormGroup label="Type" required error={touched.type && errors.type ? errors.type : undefined}>
            <SearchSelect
              selection={form.type}
              options={TYPE_OPTIONS}
              onChange={value => {
                updateForm({ type: value });
                setTouched(prev => ({ ...prev, type: true }));
              }}
              getLabel={prettifyAssetTransactionTypeEnum}
              data-testid="adjust-market-account-positions-type"
            />
          </FormGroup>

          <FormGroup
            label="Market Account"
            required
            error={touched.marketAccount && errors.marketAccount ? errors.marketAccount : undefined}
          >
            <SearchSelect
              selection={form.marketAccount}
              options={marketAccountOptions}
              onChange={value => {
                updateForm({ marketAccount: value });
                setTouched(prev => ({ ...prev, marketAccount: true }));
              }}
              getLabel={getMarketAccountLabel}
              data-testid="adjust-market-account-positions-market-account"
            />
          </FormGroup>

          <HStack gap="spacing16" alignItems="flex-start">
            <FormGroup
              label="Asset"
              required
              flexBasis="50%"
              error={touched.asset && errors.asset ? errors.asset : undefined}
            >
              <SearchSelect
                selection={form.asset}
                options={assetOptions}
                onChange={value => {
                  updateForm({ asset: value });
                  setTouched(prev => ({ ...prev, asset: true }));
                }}
                getGroup={getAssetGroup}
                showGroupTabs
                getLabel={getAssetDisplaySymbol}
                getDescription={getAssetDescription}
                data-testid="adjust-market-account-positions-asset"
                dropdownWidth={symbolSelectorDropdownWidth}
                maxHeight={400}
              />
            </FormGroup>

            <FormGroup
              label="Amount"
              required
              flexBasis="50%"
              error={touched.amount && errors.amount ? errors.amount : undefined}
              tooltip="Use negative values for withdrawals or reductions."
            >
              <NumberInput
                value={form.amount ?? ''}
                onChange={value => {
                  updateForm({ amount: value });
                  setTouched(prev => ({ ...prev, amount: true }));
                }}
                data-testid="adjust-market-account-positions-amount"
              />
            </FormGroup>
          </HStack>

          <FormGroup
            label="Update Timestamp"
            required
            error={touched.timestamp && errors.timestamp ? errors.timestamp : undefined}
          >
            <DateTimePicker
              value={form.timestamp ?? null}
              onChange={value => {
                updateForm({ timestamp: value ?? undefined });
                setTouched(prev => ({ ...prev, timestamp: true }));
              }}
              portalize={false}
              showMilliseconds
              shortcuts={{ [NOW]: 'Now' }}
              data-testid="adjust-market-account-positions-timestamp"
            />
          </FormGroup>

          <Box my="spacing4">
            <TextDividerSimple text="Allocation" />
          </Box>
          <FormGroup label="Allocation Mode" required mb="spacing8">
            <ToggleHorizontal
              options={ALLOCATION_MODE_OPTIONS}
              value={form.allocationMode}
              onChange={mode => updateForm({ allocationMode: mode })}
              size={FormControlSizes.Small}
              data-testid="adjust-market-account-positions-allocation-mode"
            />
          </FormGroup>

          {form.allocationMode === 'manual' && (
            <AllocationsSelector
              subAccountAllocations={form.subAccountAllocations}
              allocationValueType={form.allocationValueType}
              subAccountOptions={subAccountOptions}
              subAccountsByName={subAccountsByName}
              onAllocationsChange={handleAllocationsChange}
              onAllocationsValueTypeChange={handleAllocationsValueTypeChange}
              useAllocations
              showAddAllocationsButton
              required
              touched={{ subAccountAllocations: touched.subAccountAllocations }}
              errors={{ subAccountAllocations: errors.subAccountAllocations }}
              quantityCurrency={form.asset}
            />
          )}
        </Stack>
      </DialogContent>
      <Box style={{ order: 3 }}>
        <DialogFooter
          showCancel
          showConfirm
          confirmLabel="Post"
          confirmDisabled={!isEmpty(errors) || isLoading}
          confirmLoading={isLoading}
          handleConfirm={() => void handleConfirm()}
          handleCancel={disclosure.close}
          onConfirmMouseOver={setAllTouched}
        />
      </Box>
    </>
  );
};
